/**
 * SectionErrorBoundary — per-section isolation guard for the report body.
 *
 * THE BUG IT FIXES — David's Redhead BTM feasibility report rendered six
 * narrative sections (Project Intent, BTM Tech Overview, Transport,
 * Connectivity, Siting Notes, Climate & Severe Weather) BLANK even though
 * their data was present and up to date. The report body in
 * SiteRagReportView had NO React error boundary, so a single throw inside
 * ANY child of the report tree (notably a chart wrapper that measured a
 * 0-height box and threw, or a missing-global like ResizeObserver in the
 * paginated/export flow) unmounted the WHOLE report subtree — blanking
 * every sibling section, not just the one that threw.
 *
 * This boundary wraps each inline section body (and the per-type
 * NewTypeSectionsRouter) so a thrown child renders a small, visible
 * fallback IN PLACE of just that section, and its siblings keep painting.
 * A chart/component throw can NEVER blank a sibling prose section again.
 *
 * Design notes:
 *   - Renders a compact, theme-neutral fallback (NOT a full-screen panel)
 *     so the surrounding deck/page flow keeps its footprint.
 *   - In `viewMode==='export'` / print it still renders the fallback (a
 *     visible note) rather than nothing, so the PDF never carries a silent
 *     gap — but it never throws upward, so the export pipeline's
 *     data-report-ready stamp is unaffected.
 *   - Logs to console so a minified prod stack stays diagnosable via the
 *     sourcemap recipe.
 */
import React from 'react';
import { isEditorialLayoutsEnabled } from '../../../lib/runtimeFlags';

interface Props {
  /** Stable id of the wrapped section (used in the fallback + the log). */
  sectionId: string;
  /** Human label for the fallback copy (defaults to the id). */
  label?: string;
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  message: string | null;
}

export class SectionErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, message: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, message: error?.message ?? 'Unknown render error' };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[SectionErrorBoundary] section failed to render', {
      sectionId: this.props.sectionId,
      message: error?.message,
      componentStack: info?.componentStack,
    });
  }

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;
    const { sectionId, label } = this.props;
    // N1.5C empty-state flatten (EDITORIAL-MODE ONLY) — when the editorial flag
    // is ON, the fallback reads as an italic muted note on the cream page behind
    // a thin accent LEFT-rule (matching the SectionShell / LcoeChart /
    // EditorialRecommendations empty-state treatment), instead of a grey
    // `bg-surface-2` filled + bordered box. Flag OFF keeps the prior box so the
    // flag-OFF reportSurfaceParity baseline stays byte-stable.
    const editorial = isEditorialLayoutsEnabled();
    return (
      <div
        data-section={sectionId}
        data-section-id={sectionId}
        data-testid={`section-error-${sectionId}`}
        role="note"
        className={
          editorial
            ? 'my-6 pl-3 py-2 text-xs italic text-text-muted border-l-[3px] border-primary/60'
            : 'my-6 px-3 py-2 text-xs italic text-text-muted border border-border-subtle bg-surface-2/40'
        }
        {...(editorial ? { style: { background: 'transparent' } } : {})}
      >
        {label ?? sectionId} could not be rendered. The underlying data is
        present; try refreshing, or regenerate this section.
      </div>
    );
  }
}
